import React, { useEffect, useMemo } from "react";
import { Skeleton } from "@jem-open/jem-ui";
// sonner is a transitive dependency of @jem-open/jem-ui (peer dep)
// eslint-disable-next-line import/no-extraneous-dependencies
import { toast } from "sonner";
import { PreviewParams } from "../components/reportPreview/types";
import { ReportType } from "../services/model/ReportType";
import PlannedVsActualsPreview from "../components/reportPreview/PlannedVsActualsPreview";
import TimesheetPreview from "../components/reportPreview/TimesheetPreview";
import PostingSheetPreview from "../components/reportPreview/PostingSheetPreview";
import {
  usePlannedVsActualsPreviewApiService,
  usePostingSheetPreviewApiService,
  useTimesheetPreviewApiService,
} from "../services/api/ReportPreviewApiService";
import {
  formatIsoDate,
  triggerBlobDownload,
  usePlannedVsActualsReportApiService,
  usePostingSheetReportApiService,
  useTimesheetReportApiService,
} from "../services/api/ReportGenerationApiService";
import { filenameFor } from "../utils/filename";
import { ReportScope } from "../services/model/ReportScope";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import NotFound from "../../../common/pages/NotFound";

function parseParams(search: string): PreviewParams | null {
  const p = new URLSearchParams(search);
  const type = p.get("type");
  if (
    type !== "planned-vs-actuals" &&
    type !== "timesheet" &&
    type !== "posting-sheet"
  ) {
    return null;
  }
  const scopeKind = p.get("scopeKind");
  if (
    scopeKind !== "site" &&
    scopeKind !== "roster" &&
    scopeKind !== "region"
  ) {
    return null;
  }
  const scopeId = p.get("scopeId") ?? "";
  const scopeLabel = p.get("scopeLabel") ?? "";
  const shift = p.get("shift") ?? "";
  const parse = (key: string): Date | undefined => {
    const v = p.get(key);
    if (!v) return undefined;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? undefined : d;
  };
  return {
    type: type as ReportType,
    scopeKind,
    scopeId,
    scopeLabel,
    scopeRegion: p.get("scopeRegion") ?? undefined,
    scopeParent: p.get("scopeParent") ?? undefined,
    shift,
    dateFrom: parse("dateFrom"),
    dateTo: parse("dateTo"),
    date: parse("date"),
  };
}

const PreviewPaper = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => (
  <div className="max-w-[960px] mx-auto bg-white shadow-sm sm:my-lg sm:rounded-lg">
    <div className="px-md sm:px-lg py-lg">{children}</div>
  </div>
);

const PreviewSkeleton = (): JSX.Element => (
  <div className="space-y-md">
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-4 w-2/3" />
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-20" />
      ))}
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
);

interface PreviewErrorProps {
  message: string;
}

const PreviewError = ({ message }: PreviewErrorProps): JSX.Element => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-md">
    <p className="text-sm font-semibold text-red-700">
      Couldn&apos;t load the preview
    </p>
    <p className="text-xs text-red-600 mt-xxxs">{message}</p>
  </div>
);

const ReportPreviewPage = (): JSX.Element => {
  const reportsEnabled = useEnabledFeatureFlag(FeatureFlags.REPORTS_AGENT_UI);
  const params = useMemo(() => parseParams(window.location.search), []);

  useEffect(() => {
    if (!params) {
      document.title = "Report preview";
      return;
    }
    let typeLabel = "Posting Sheet";
    if (params.type === "planned-vs-actuals") typeLabel = "Planned vs Actuals";
    else if (params.type === "timesheet") typeLabel = "Timesheet";
    document.title = `${typeLabel} — ${params.scopeLabel}`;
  }, [params]);

  const scopePayload = useMemo(() => {
    if (!params) return null;
    return { kind: params.scopeKind, id: params.scopeId };
  }, [params]);

  const dateRangeBody = useMemo(() => {
    if (!params || !scopePayload || !params.dateFrom || !params.dateTo) {
      return null;
    }
    return {
      scope: scopePayload,
      date_from: formatIsoDate(params.dateFrom),
      date_to: formatIsoDate(params.dateTo),
      shift_name: params.shift && params.shift.length > 0 ? params.shift : null,
    };
  }, [params, scopePayload]);

  const postingSheetBody = useMemo(() => {
    if (!params || !scopePayload || !params.date || !params.shift) return null;
    return {
      scope: scopePayload,
      date: formatIsoDate(params.date),
      shift_name: params.shift,
    };
  }, [params, scopePayload]);

  const plannedQuery = usePlannedVsActualsPreviewApiService(
    dateRangeBody ?? {
      scope: { kind: "roster", id: "" },
      date_from: "1970-01-01",
      date_to: "1970-01-01",
      shift_name: null,
    },
    params?.type === "planned-vs-actuals" && dateRangeBody !== null
  );

  const timesheetQuery = useTimesheetPreviewApiService(
    dateRangeBody ?? {
      scope: { kind: "roster", id: "" },
      date_from: "1970-01-01",
      date_to: "1970-01-01",
      shift_name: null,
    },
    params?.type === "timesheet" && dateRangeBody !== null
  );

  const postingQuery = usePostingSheetPreviewApiService(
    postingSheetBody ?? {
      scope: { kind: "roster", id: "" },
      date: "1970-01-01",
      shift_name: "",
    },
    params?.type === "posting-sheet" && postingSheetBody !== null
  );

  const plannedVsActualsDownload = usePlannedVsActualsReportApiService();
  const timesheetDownload = useTimesheetReportApiService();
  const postingSheetDownload = usePostingSheetReportApiService();

  if (!reportsEnabled) {
    return <NotFound />;
  }

  if (!params) {
    return (
      <div className="min-h-screen flex items-center justify-center p-lg">
        <div className="max-w-[420px] text-center">
          <h1 className="text-lg font-semibold text-greyscale-text-title">
            Missing or invalid preview parameters
          </h1>
          <p className="text-sm text-greyscale-text-body mt-xs">
            This tab was opened without the required query string. Close it and
            try again from the Reports wizard.
          </p>
        </div>
      </div>
    );
  }

  let scopeForFilename: ReportScope;
  if (params.scopeKind === "site") {
    scopeForFilename = {
      kind: "site",
      id: params.scopeId,
      label: params.scopeLabel,
      region: params.scopeRegion,
    };
  } else if (params.scopeKind === "region") {
    scopeForFilename = {
      kind: "region",
      id: params.scopeId,
      label: params.scopeLabel,
    };
  } else {
    scopeForFilename = {
      kind: "roster",
      id: params.scopeId,
      label: params.scopeLabel,
      parentSite: params.scopeParent,
    };
  }

  const handleDownload = (): void => {
    const filename = filenameFor(
      params.type,
      scopeForFilename,
      params.dateFrom && params.dateTo
        ? { from: params.dateFrom, to: params.dateTo }
        : undefined,
      params.date,
      params.shift && params.shift.length > 0 ? params.shift : null
    );

    const onSuccess = (blob: Blob): void => {
      triggerBlobDownload(blob, filename);
      toast.success(`Downloaded ${filename}`);
    };
    const onError = (): void => {
      toast.error("Download failed", {
        description: "The service returned an error. Please try again.",
      });
    };

    if (params.type === "posting-sheet" && postingSheetBody) {
      postingSheetDownload.mutate(postingSheetBody, { onSuccess, onError });
    } else if (params.type === "planned-vs-actuals" && dateRangeBody) {
      plannedVsActualsDownload.mutate(dateRangeBody, { onSuccess, onError });
    } else if (params.type === "timesheet" && dateRangeBody) {
      timesheetDownload.mutate(dateRangeBody, { onSuccess, onError });
    }
  };

  const dateRangeMissingError = (
    <PreviewError message="This report needs a date range. Re-open it from the Reports wizard." />
  );
  const postingMissingError = (
    <PreviewError message="This report needs a date and shift. Re-open it from the Reports wizard." />
  );

  let body: JSX.Element;

  if (params.type === "planned-vs-actuals") {
    if (!dateRangeBody) {
      body = dateRangeMissingError;
    } else if (plannedQuery.error) {
      body = <PreviewError message={plannedQuery.error.message} />;
    } else if (plannedQuery.isLoading || !plannedQuery.data) {
      body = <PreviewSkeleton />;
    } else {
      body = (
        <PlannedVsActualsPreview
          params={params}
          data={plannedQuery.data}
          onDownload={handleDownload}
        />
      );
    }
  } else if (params.type === "timesheet") {
    if (!dateRangeBody) {
      body = dateRangeMissingError;
    } else if (timesheetQuery.error) {
      body = <PreviewError message={timesheetQuery.error.message} />;
    } else if (timesheetQuery.isLoading || !timesheetQuery.data) {
      body = <PreviewSkeleton />;
    } else {
      body = (
        <TimesheetPreview
          params={params}
          data={timesheetQuery.data}
          onDownload={handleDownload}
        />
      );
    }
  } else if (!postingSheetBody) {
    body = postingMissingError;
  } else if (postingQuery.error) {
    body = <PreviewError message={postingQuery.error.message} />;
  } else if (postingQuery.isLoading || !postingQuery.data) {
    body = <PreviewSkeleton />;
  } else {
    body = (
      <PostingSheetPreview
        params={params}
        data={postingQuery.data}
        onDownload={handleDownload}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PreviewPaper>{body}</PreviewPaper>
    </div>
  );
};

export default ReportPreviewPage;
